import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
export interface ClinicInfo {
  name: string;
  phone: string;
  email: string;
  address: string;
  cnpj: string;
  logo_url?: string;
  maps_url?: string;
  latitude?: number;
  longitude?: number;
}

export interface BusinessHours {
  start: string;
  end: string;
  interval: number;
  days: number[];
  saturday_end?: string;
}

export interface AppointmentSettings {
  default_duration: number;
  allow_overlap: boolean;
  buffer_time: number;
}

export interface NotificationSettings {
  email_enabled: boolean;
  sms_enabled: boolean;
  reminder_hours: number;
  confirm_on_schedule: boolean;
  confirm_on_cancel: boolean;
}

export interface AppearanceSettings {
  primary_color: string;
  theme: string;
}

export interface SecuritySettings {
  min_password_length: number;
  require_uppercase: boolean;
  require_number: boolean;
  require_special: boolean;
  session_timeout: number;
  logout_on_inactive: boolean;
}

export interface PushNotificationSettings {
  enabled: boolean;
  vapid_public_key: string;
  vapid_private_key: string;
  vapid_subject?: string;
}

export interface SmtpSettings {
  enabled: boolean;
  from_name: string;
  from_email: string;
  reply_to?: string;
  use_tls: boolean;
}

export interface LandingPageContent {
  hero: {
    headline: string;
    headlineHighlight: string;
    subheadline: string;
    ctaPrimary: string;
    ctaSecondary: string;
    whatsappNumber: string;
    badges: string[];
  };
  services: {
    title: string;
    titleHighlight: string;
    subtitle: string;
  };
  features: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    items: { title: string; description: string }[];
  };
  howItWorks: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    steps: { title: string; description: string }[];
  };
  contact: {
    title: string;
    titleHighlight: string;
    subtitle: string;
  };
}

export type SettingKey = 
  | 'clinic_info' 
  | 'business_hours' 
  | 'appointment_settings' 
  | 'notifications' 
  | 'appearance' 
  | 'security'
  | 'push_notifications'
  | 'smtp_settings'
  | 'landing_page_content'
  | 'landing_page_enabled';

export type SettingValue = 
  | ClinicInfo 
  | BusinessHours 
  | AppointmentSettings 
  | NotificationSettings 
  | AppearanceSettings 
  | SecuritySettings
  | PushNotificationSettings
  | SmtpSettings
  | LandingPageContent
  | boolean;

interface SystemSetting {
  id: string;
  key: string;
  value: unknown;
  category: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export function useSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*');

      if (error) throw error;
      
      // Transform array to object for easier access
      const settingsMap: Record<string, SystemSetting> = {};
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting as SystemSetting;
      });
      
      return settingsMap;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: SettingKey; value: SettingValue }) => {
      const { data, error } = await supabase
        .from('system_settings')
        .update({ value: value as unknown as Json })
        .eq('key', key)
        .select('key');

      if (error) throw error;
      // Sem linha atualizada (chave ausente ou RLS) não é sucesso.
      if (!data || data.length === 0) throw new Error('Nenhuma configuração foi atualizada');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('Configuração salva com sucesso!');
    },
    onError: (error) => {
      console.error('Error updating setting:', error);
      toast.error('Erro ao salvar configuração');
    },
  });

  const getSetting = useCallback(<T extends SettingValue>(key: SettingKey): T | null => {
    if (!settings || !settings[key]) return null;
    return settings[key].value as T;
  }, [settings]);

  const updateSetting = useCallback((key: SettingKey, value: SettingValue) => {
    updateMutation.mutate({ key, value });
  }, [updateMutation]);

  /** Igual a updateSetting, mas aguardável: resolve só após gravação confirmada, rejeita em erro. */
  const saveSetting = useCallback(async (key: SettingKey, value: SettingValue) => {
    await updateMutation.mutateAsync({ key, value });
  }, [updateMutation]);

  return {
    settings,
    isLoading,
    error,
    getSetting,
    updateSetting,
    saveSetting,
    isUpdating: updateMutation.isPending,
  };
}
